import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, RefreshCw, Inbox, Bookmark, ClipboardCheck, Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { api } from "@/lib/api";
import { PRODUCTS } from "@/data/catalogue";

const statusColors = {
  new: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  in_progress: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  closed: "bg-slate-500/15 text-slate-400 border-slate-500/30",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [interest, setInterest] = useState([]);
  const [selfChecks, setSelfChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productFilter, setProductFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const logout = useCallback(() => {
    localStorage.removeItem("bigtek_admin_token");
    navigate("/admin/login", { replace: true });
  }, [navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, e, i, sc] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/enquiries", { params: { type: "enquiry" } }),
        api.get("/admin/enquiries", { params: { type: "interest" } }),
        api.get("/admin/self-checks"),
      ]);
      setStats(s.data);
      setEnquiries(e.data);
      setInterest(i.data);
      setSelfChecks(sc.data);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Session expired, please sign in.");
        logout();
      } else {
        toast.error("Failed to load data");
      }
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (!localStorage.getItem("bigtek_admin_token")) {
      navigate("/admin/login", { replace: true });
      return;
    }
    load();
  }, [load, navigate]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/enquiries/${id}/status`, { status });
      setEnquiries((prev) => prev.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success("Status updated");
    } catch {
      toast.error("Could not update status");
    }
  };

  const filteredEnquiries = enquiries.filter(
    (e) =>
      (productFilter === "all" || e.product_slug === productFilter) &&
      (statusFilter === "all" || e.status === statusFilter)
  );

  const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" }) : "—");

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-10 border-b border-border bg-background/90 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 lg:px-8">
          <div className="flex items-baseline gap-3">
            <span className="font-display text-lg font-bold tracking-tight">BIGTEK</span>
            <span className="text-xs uppercase tracking-widest-xs text-muted-foreground">Admin</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" data-testid="admin-refresh" onClick={load} className="rounded-sm">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} strokeWidth={1.5} />
            </Button>
            <Button variant="ghost" size="sm" data-testid="admin-logout" onClick={logout} className="rounded-sm">
              <LogOut className="mr-2 h-4 w-4" strokeWidth={1.5} /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-5 py-10 lg:px-8">
        {/* Stats */}
        <div className="grid grid-cols-2 gap-px border border-border bg-border lg:grid-cols-4">
          <Stat icon={Inbox} label="Enquiries" value={stats?.enquiries} />
          <Stat icon={Bookmark} label="Interest" value={stats?.interest} />
          <Stat icon={Bell} label="New / unactioned" value={stats?.new} />
          <Stat icon={ClipboardCheck} label="Self-checks" value={stats?.self_checks} />
        </div>

        <Tabs defaultValue="enquiries" className="mt-10">
          <TabsList className="rounded-sm">
            <TabsTrigger value="enquiries" data-testid="tab-enquiries" className="rounded-sm">
              Enquiries ({enquiries.length})
            </TabsTrigger>
            <TabsTrigger value="interest" data-testid="tab-interest" className="rounded-sm">
              Interest ({interest.length})
            </TabsTrigger>
            <TabsTrigger value="selfchecks" data-testid="tab-selfchecks" className="rounded-sm">
              Self-checks ({selfChecks.length})
            </TabsTrigger>
          </TabsList>

          {/* Enquiries */}
          <TabsContent value="enquiries" className="mt-6">
            <div className="mb-4 flex flex-wrap gap-3">
              <Select value={productFilter} onValueChange={setProductFilter}>
                <SelectTrigger data-testid="filter-product" className="w-64 rounded-sm">
                  <SelectValue placeholder="Product" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All products</SelectItem>
                  {PRODUCTS.filter((p) => p.tier !== "roadmap").map((p) => (
                    <SelectItem key={p.slug} value={p.slug}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="filter-status" className="w-44 rounded-sm">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="in_progress">In progress</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <EnquiryTable rows={filteredEnquiries} fmt={fmt} onStatus={updateStatus} withStatus />
          </TabsContent>

          {/* Interest */}
          <TabsContent value="interest" className="mt-6">
            <EnquiryTable rows={interest} fmt={fmt} onStatus={updateStatus} withStatus />
          </TabsContent>

          {/* Self-checks */}
          <TabsContent value="selfchecks" className="mt-6">
            <div className="overflow-x-auto border border-border">
              <Table data-testid="selfchecks-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Organisation</TableHead>
                    <TableHead>Band</TableHead>
                    <TableHead className="text-right">Score</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selfChecks.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="py-10 text-center text-muted-foreground">No self-checks yet.</TableCell></TableRow>
                  ) : selfChecks.map((s) => (
                    <TableRow key={s.id} data-testid={`selfcheck-row-${s.id}`}>
                      <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{fmt(s.created_at)}</TableCell>
                      <TableCell className="font-medium">{s.name || "—"}</TableCell>
                      <TableCell className="text-sm">{s.email}</TableCell>
                      <TableCell className="text-sm">{s.organisation || "—"}</TableCell>
                      <TableCell><Badge variant="outline" className="rounded-full">{s.band}</Badge></TableCell>
                      <TableCell className="text-right font-mono text-sm">{s.percent}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function useCountUp(target, duration = 950) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (typeof target !== "number" || !isFinite(target)) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setVal(target);
      return;
    }
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setVal(Math.round(target * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return val;
}

function Stat({ icon: Icon, label, value }) {
  const display = useCountUp(value);
  const ready = typeof value === "number";
  return (
    <div className="bg-background p-6">
      <Icon className="h-5 w-5 text-[hsl(var(--brand))]" strokeWidth={1.5} />
      <div className="mt-3 font-display text-3xl font-bold tabular-nums" data-testid={`stat-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
        {ready ? display : "—"}
      </div>
      <div className="mt-1 text-xs uppercase tracking-widest-xs text-muted-foreground">{label}</div>
    </div>
  );
}

function EnquiryTable({ rows, fmt, onStatus, withStatus }) {
  return (
    <div className="overflow-x-auto border border-border">
      <Table data-testid="enquiries-table">
        <TableHeader>
          <TableRow>
            <TableHead>Date</TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Organisation</TableHead>
            <TableHead>Product</TableHead>
            <TableHead>Message</TableHead>
            {withStatus && <TableHead className="w-40">Status</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow><TableCell colSpan={withStatus ? 6 : 5} className="py-10 text-center text-muted-foreground">No records.</TableCell></TableRow>
          ) : rows.map((r) => (
            <TableRow key={r.id} data-testid={`enquiry-row-${r.id}`}>
              <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{fmt(r.created_at)}</TableCell>
              <TableCell>
                <div className="font-medium">{r.name}</div>
                <div className="text-xs text-muted-foreground">{r.email}</div>
              </TableCell>
              <TableCell className="text-sm">
                <div>{r.organisation}</div>
                <div className="text-xs text-muted-foreground">{r.org_type}</div>
              </TableCell>
              <TableCell className="max-w-[180px] text-sm">{r.product_name || "General"}</TableCell>
              <TableCell className="max-w-[280px] text-xs text-muted-foreground">{r.message || "—"}</TableCell>
              {withStatus && (
                <TableCell>
                  <Select value={r.status} onValueChange={(v) => onStatus(r.id, v)}>
                    <SelectTrigger data-testid={`status-select-${r.id}`} className={`h-8 rounded-sm border text-xs ${statusColors[r.status] || ""}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="in_progress">In progress</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
